import React, { useEffect } from 'react';
import type {
  MultiValue,
  MultiValueGenericProps,
  MultiValueProps,
  OnChangeValue,
} from 'react-select';
import Select, { components } from 'react-select';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface ReactSelectOption {
  value: string;
  label: string;
  badge?: string;
}

const SortableMultiValue = (props: MultiValueProps<ReactSelectOption>) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.data.value });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const onMouseDown: React.MouseEventHandler<HTMLDivElement> = e => {
    const target = e.target as HTMLElement;
    if (target.closest('.test-select__multi-value__remove')) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  };
  const innerProps = { ...props.innerProps, onMouseDown };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, width: '100%' }}
      {...attributes}
      {...listeners}
    >
      <components.MultiValue {...props} innerProps={innerProps} />
    </div>
  );
};

const SortableMultiValueLabel = (props: MultiValueGenericProps) => (
  <components.MultiValueLabel {...props} />
);

interface MultiSelectSortableProps {
  options: ReactSelectOption[];
  defaultValue: ReactSelectOption[];
  onChange: (options: MultiValue<ReactSelectOption>) => void;
}

export const MultiSelectSortable: React.FC<MultiSelectSortableProps> = ({
  options,
  defaultValue,
  onChange,
}) => {
  const [selected, setSelected] =
    React.useState<readonly ReactSelectOption[]>(defaultValue);

  useEffect(() => {
    setSelected(defaultValue);
  }, [defaultValue]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const handleOnChange = (
    selectedOptions: OnChangeValue<ReactSelectOption, true>,
  ) => {
    setSelected(selectedOptions);
    onChange(selectedOptions);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = selected.findIndex(o => o.value === active.id);
    const newIndex = selected.findIndex(o => o.value === over.id);
    const newValue = arrayMove([...selected], oldIndex, newIndex);
    setSelected(newValue);
    onChange(newValue);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={selected.map(o => o.value)}
        strategy={verticalListSortingStrategy}
      >
        <Select<ReactSelectOption, true>
          className="test-multi-select"
          classNamePrefix="test-select"
          styles={{
            valueContainer: provided => ({
              ...provided,
              flexDirection: 'column',
              alignItems: 'stretch',
            }),
            multiValue: provided => ({
              ...provided,
              width: '100%',
            }),
            multiValueLabel: provided => ({
              ...provided,
              width: '100%',
            }),
          }}
          isMulti
          options={options}
          value={selected}
          onChange={handleOnChange}
          placeholder={'Wskaż...'}
          components={{
            MultiValue: SortableMultiValue as unknown as React.ComponentType<
              MultiValueProps<ReactSelectOption, true>
            >,
            MultiValueLabel:
              SortableMultiValueLabel as unknown as React.ComponentType<
                MultiValueGenericProps<ReactSelectOption, true>
              >,
          }}
          closeMenuOnSelect={false}
        />
      </SortableContext>
    </DndContext>
  );
};

export default MultiSelectSortable;
